'use client';

/**
 * MBG Nexus Authentication
 *
 * Stage 1: Username lookup via get_office_login_email RPC (office accounts)
 *          OR get_auth_credentials_by_username RPC (members)
 * Stage 2: Supabase Auth signInWithPassword using the actual email from auth.users
 * Stage 3: Session verification
 * Stage 4: Role-based routing (role resolved from DB by AuthContext — not from JWT)
 * Stage 5: Non-blocking audit logging (fire-and-forget)
 */

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { Eye, EyeOff, Lock, User, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import GoldButton from '@/components/ui/GoldButton';
import { createClient } from '@/lib/supabase/client';
import { ROLE_ROUTES, normalizeRole } from '@/lib/auth/roles';

interface LoginFormData {
  username: string;
  password: string;
  remember: boolean;
}

// Stage 5: Fire-and-forget audit log — NEVER blocks authentication
function recordLoginEvent(
  supabase: ReturnType<typeof createClient>,
  userId: string,
  username: string,
  success: boolean,
  failureReason?: string
): void {
  Promise.resolve()
    .then(() =>
      supabase.rpc('record_iam_login_event', {
        p_user_id:       userId,
        p_username:      username,
        p_success:       success,
        p_failure_reason: failureReason ?? null,
      })
    )
    .catch((err) => {
      console.warn('[Audit] record_iam_login_event failed (non-blocking):', err);
    });
}

interface LoginFormProps {
  onSwitchToRegister: () => void;
}

export default function LoginForm({ onSwitchToRegister }: LoginFormProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError]     = useState<string | null>(null);
  const router = useRouter();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormData>({ defaultValues: { remember: false } });

  const onSubmit = async (data: LoginFormData) => {
    setLoginError(null);
    const supabase = createClient();
    const username = data.username.trim().toUpperCase();

    // ─────────────────────────────────────────────────────────────
    // STAGE 1: Resolve login email from username
    // ─────────────────────────────────────────────────────────────
    let loginEmail: string | null = null;
    let credRole: string | null   = null;
    let credUserId: string | null = null;
    let forcePasswordChange       = false;

    try {
      // Try office account lookup first
      const { data: officeResult, error: officeError } = await supabase.rpc(
        'get_office_login_email',
        { p_username: username }
      );

      if (officeError) {
        console.warn('[Auth] get_office_login_email RPC error:', officeError.message);
        // Fall through to member lookup
      } else if (officeResult && officeResult.found === true) {
        if (officeResult.locked) {
          setLoginError(officeResult.error || 'Account is locked. Contact the CEO.');
          return;
        }
        if (officeResult.inactive) {
          setLoginError(officeResult.error || 'Account is inactive. Contact the CEO.');
          return;
        }
        loginEmail          = officeResult.email as string;
        credRole            = officeResult.role as string;
        credUserId          = officeResult.auth_user_id as string;
        forcePasswordChange = officeResult.force_password_change === true;
      } else if (officeResult && officeResult.found === false) {
        // Not an office account — try member lookup
        const { data: memberResult, error: memberError } = await supabase.rpc(
          'get_auth_credentials_by_username',
          { p_username: username }
        );

        if (memberError) {
          console.warn('[Auth] get_auth_credentials_by_username error:', memberError.message);
          loginEmail = `${username.toLowerCase()}@mbgnexus.internal`;
        } else if (memberResult && memberResult.found === true) {
          if (memberResult.locked) {
            setLoginError(memberResult.error || 'Account is locked. Contact your administrator.');
            return;
          }
          if (memberResult.suspended) {
            setLoginError(memberResult.error || 'Account is suspended. Contact your administrator.');
            return;
          }
          loginEmail          = memberResult.email as string;
          credRole            = memberResult.role as string;
          credUserId          = memberResult.auth_id as string;
          forcePasswordChange = memberResult.force_password_change === true;
        } else {
          setLoginError('Invalid username or password. Please try again.');
          return;
        }
      }
    } catch (lookupErr) {
      console.warn('[Auth] Lookup exception (continuing with fallback):', lookupErr);
      loginEmail = `${username.toLowerCase()}@mbgnexus.internal`;
    }

    if (!loginEmail) {
      setLoginError('Invalid username or password. Please try again.');
      return;
    }

    // ─────────────────────────────────────────────────────────────
    // STAGE 2: Supabase Auth sign-in with the resolved email
    // ─────────────────────────────────────────────────────────────
    const { data: authData, error: signInError } = await supabase.auth.signInWithPassword({
      email:    loginEmail,
      password: data.password,
    });

    if (signInError) {
      if (credUserId) {
        recordLoginEvent(supabase, credUserId, username, false, signInError.message);
      }
      setLoginError('Invalid username or password. Please try again.');
      return;
    }

    // ─────────────────────────────────────────────────────────────
    // STAGE 3: Session verification
    // ─────────────────────────────────────────────────────────────
    if (!authData.session || !authData.user) {
      setLoginError('Session could not be created. Please try again.');
      return;
    }

    const userId = authData.user.id;

    // Stage 5: Non-blocking audit log
    recordLoginEvent(supabase, userId, username, true);

    // ─────────────────────────────────────────────────────────────
    // STAGE 4: Role-based routing
    //
    // AuthContext resolves the authoritative role from the DB via
    // get_authenticated_user_role() after onAuthStateChange fires.
    // LoginForm routes based on credRole (from the DB lookup RPC).
    // JWT user_metadata.role is NOT used as an application-role authority.
    // PortalGuard will re-evaluate once AuthContext has the DB-resolved role.
    // ─────────────────────────────────────────────────────────────
    const role  = normalizeRole(credRole) || 'member';
    const route = ROLE_ROUTES[role] ?? '/member-portal';

    if (forcePasswordChange) {
      try {
        sessionStorage.setItem('mbg_pending_username', username);
        sessionStorage.setItem('mbg_pending_role', role);
      } catch {
        // sessionStorage may be unavailable in some environments
      }
      router.push('/change-password');
      return;
    }

    toast.success('Welcome back! Redirecting to your portal...');
    router.push(route);
  };

  return (
    <div>
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-foreground mb-2">Welcome back</h2>
        <p className="text-sm text-muted-foreground">Sign in to your MBG Nexus account</p>
      </div>

      {/* Login error */}
      {loginError && (
        <div className="flex items-start gap-3 p-3 rounded-xl bg-red-500/10 border border-red-500/20 mb-6">
          <AlertCircle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
          <p className="text-xs text-red-400">{loginError}</p>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        {/* Username */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">Username</label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Enter your username"
              className="input-field pl-10 font-mono uppercase"
              autoComplete="username"
              {...register('username', {
                required: 'Username is required',
              })}
            />
          </div>
          {errors.username && (
            <p className="mt-1.5 text-xs text-danger flex items-center gap-1">
              <AlertCircle className="w-3 h-3" /> {errors.username.message}
            </p>
          )}
        </div>

        {/* Password */}
        <div>
          <label className="text-sm font-medium text-foreground block mb-2">Password</label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type={showPassword ? 'text' : 'password'}
              placeholder="••••••••"
              className="input-field pl-10 pr-10"
              autoComplete="current-password"
              {...register('password', {
                required: 'Password is required',
                minLength: { value: 6, message: 'Minimum 6 characters' },
              })}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          {errors.password && (
            <p className="mt-1.5 text-xs text-danger flex items-center gap-1">
              <AlertCircle className="w-3 h-3" /> {errors.password.message}
            </p>
          )}
        </div>

        {/* Remember me */}
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="remember"
            className="w-4 h-4 rounded border-border accent-gold"
            {...register('remember')}
          />
          <label htmlFor="remember" className="text-sm text-muted-foreground cursor-pointer">
            Remember me
          </label>
        </div>

        <GoldButton type="submit" loading={isSubmitting} className="w-full">
          Sign In
        </GoldButton>
      </form>

      <div className="mt-6 text-center">
        <p className="text-sm text-muted-foreground">
          New member?{' '}
          <button
            onClick={onSwitchToRegister}
            className="text-gold hover:text-gold-light font-medium transition-colors"
          >
            Create account
          </button>
        </p>
      </div>
    </div>
  );
}